import { useCallback, useEffect, useRef, useState } from "react";

// De ranglijsten houden hun oude volgorde vast tot de stijger in beeld komt.
//
// De nieuwe cijfers staan er meteen, maar de verkoop die ze veroorzaakte wordt
// pas daarna verteld: eerst de rand, de aanloop, de aankondiging, de balk, en
// helemaal aan het eind de stijger (zie saleGroup in useSaleEvents). Schuift
// het podium al om zodra de cijfers binnen zijn, dan zie je de wissel vóór de
// rand oplicht en is de stijger achteraf geen nieuws meer. Dus: verandert de
// volgorde, dan blijft de oude lijst staan, met de oude bedragen erin, tot er
// een "rank-up" loopt. Op dat moment komt de nieuwe lijst en schuift het
// podium door (useFlipReorder), tegelijk met het kaartje en het veren.
//
// Nooit langer dan nodig: blijft het settleMs stil zonder dat er een stijger
// kwam (alles gestopt, of een wisseling waar geen animatie bij hoort), dan
// komt de nieuwe lijst alsnog. En een naam die er nog niet stond laat meteen
// alles door: die rij moet er zijn voor de rij-oplichter hem zoekt.
//
// settle(data): zet de getoonde lijst meteen op deze cijfers, zonder
// vasthouden. Voor het lab, waar R de cijfers terugzet: dat is geen verkoop,
// en een verkoop die er direct achteraan komt moet weer vanaf de begincijfers
// vastgehouden worden.

const settleMs = 1500;

const orderOf = (list = []) => list.map((row) => row.name).join("\n");
const listsOf = (data) => ({ rankList: data.rankList, yearRankList: data.yearRankList });

export function useHeldRanking(data, active) {
  const shown = useRef(listsOf(data));
  const release = useRef(false);
  const [, rerender] = useState(0);

  const known = new Set(shown.current.rankList.map((row) => row.name));
  const newcomer = data.rankList.some((row) => !known.has(row.name));
  const sameOrder = orderOf(data.rankList) === orderOf(shown.current.rankList);
  const climbing = active.some((run) => run.id === "rank-up");

  if (sameOrder || newcomer || climbing || release.current) {
    shown.current = listsOf(data);
    release.current = false;
  }

  const settle = useCallback((next) => {
    shown.current = listsOf(next);
    release.current = false;
    rerender((count) => count + 1);
  }, []);

  const holding = shown.current.rankList !== data.rankList;
  const idle = active.length === 0;

  useEffect(() => {
    if (!holding || !idle) return undefined;
    const timer = setTimeout(() => {
      release.current = true;
      rerender((count) => count + 1);
    }, settleMs);
    return () => clearTimeout(timer);
  }, [holding, idle]);

  return { ...shown.current, settle };
}
